import React, { useMemo } from 'react';
import { ChartClickInteraction } from '@components/Widgets/types';
import {
  FeatureFlags,
  FormatterType,
  getSeverityByScore,
  isValidSeverity,
  SeverityLabels,
  SeverityPrimaryColors,
} from '@fabric-ui/components';
import { generateInnerDefaultFormatter } from '@fabric-ui/components/Table';
import { Box } from '@mui/material';
import {
  getDateBucketFromExpression,
  getFieldTypeByExpression,
  isDateTruncExpression,
} from '@views/CustomDashboards/CustomDashboardsUtils/expressionUtils';
import AvLegend from '../../../../components/AvLegend';
import AvPie, { AvPieProps } from '../../../../components/Widgets/VisualizationWidgets/AvPie';
import { WidgetTooltipContent, WidgetTooltipContentProps } from '../../../../components/Widgets/WidgetTooltip/WidgetTooltip';
import { useAvContext } from '../../../../context/AvContextProvider';
import { useMetricsContextAsObject } from '../../../../hooks/useMetricsContext';
import { useFormattingRules } from '../../../FormattingRules/hooks';
import { getColorFromFormattingConditions, getFormattingRuleById, isDimensionFormattingRule } from '../../../FormattingRules/utils';
import { Measurement } from '../../../Measurements/types';
import { defaultDrillDownHierarchy, NO_VALUE } from '../../constants';
import { fieldFormatter, getSelectedPieChartData } from '../../CustomDashboardsUtils';
import { hasTimeBucket } from '../../EditCustomDashboard/TimeBucketBreakdown/utils';
import { getCustomTickFormatter, useCustomPalette } from '../../hooks';
import { useGetDisplayName } from '../../hooks/useGetDisplayName';
import { useGetFieldType } from '../../hooks/useGetFieldType';
import {
  AllTypeWidgets,
  CommonWidgetProps,
  Data,
  DonutTypeWidget,
  PieBasicLegendPosition,
  PieCategoryWidgetTypes,
  PieLegendStyles,
  PieSubType,
} from '../../types';
import { LabelFormatterOption } from '../../types/types';
import { PieSeriesAdapter } from './PieSeriesAdapter';

export interface PieWidgetProps extends CommonWidgetProps {
  widget: PieCategoryWidgetTypes;
  clickInteractions?: ChartClickInteraction[];
  customPalette?: string[];
  enableResponsiveLegend?: boolean;
  useV2?: boolean;
}

type PieWidgetDataEntry = { [x: string]: any; headCellType: string | undefined; dim?: string; metric: string; originalValue: any };

const createPieWithNeedleData = (dimension: string, metric: string, headCellType?: string): PieWidgetDataEntry[] => [
  { [dimension]: 'Low', [metric]: 25, headCellType, dimension, metric, originalValue: 25 },
  { [dimension]: 'Medium', [metric]: 25, headCellType, dimension, metric, originalValue: 25 },
  { [dimension]: 'High', [metric]: 25, headCellType, dimension, metric, originalValue: 25 },
  { [dimension]: 'Critical', [metric]: 25, headCellType, dimension, metric, originalValue: 25 },
];

export const PieWidgetImpl = (
  props: PieWidgetProps & {
    formattingRules?: ReturnType<typeof useFormattingRules>['data'];
    measurementsObject: Record<string, Measurement>;
    featureFlags?: ReturnType<typeof useAvContext>['featureFlags'];
    metricDisplayName?: string | number | null | undefined;
    getHeadCellType: ReturnType<typeof useGetFieldType>;
  }
) => {
  const {
    widget,
    data = [],
    responseTotals,
    selected = [],
    onSelect,
    clickInteractions,
    customPalette,
    featureFlags = {},
    formattingRules = [],
    measurementsObject,
    getHeadCellType,
    metricDisplayName,
    enableResponsiveLegend,
    useV2,
  } = props;

  const {
    drillDownHierarchy = defaultDrillDownHierarchy,
    requests: [
      {
        select: { dims, metrics },
      },
    ],
    definition: {
      custom: { labelFormatter },
    },
  } = widget;

  const { activeIndex } = drillDownHierarchy;
  const dimName = dims?.[0]?.name;
  const dimNameOrAlias = activeIndex !== 0 ? drillDownHierarchy.fields[activeIndex - 1]?.name : dims?.[0]?.alias || dimName;

  const metric = metrics?.[0]?.alias || metrics[0]?.name;

  const shouldDisplayNull = true;

  const dimsWithExpressions = useMemo(
    () =>
      widget.requests[0].select.dims.reduce((acc, dim) => {
        if (dim.expression) {
          return {
            ...acc,
            [dim.name]: dim,
          };
        }
        return acc;
      }, {}),
    [widget.requests[0].select.dims]
  );

  const headCellType =
    hasTimeBucket(dimNameOrAlias) || dimsWithExpressions[dimName]
      ? FormatterType.historicDate
      : getHeadCellType({ entityFieldKey: dimNameOrAlias });

  const getFormattedDimensionValue = (datum: Record<string, any>) => {
    // Handle dimension with an expression
    if (dimsWithExpressions[dimName]) {
      return generateInnerDefaultFormatter({
        value: datum[dimName],
        type: getFieldTypeByExpression(dimsWithExpressions[dimName].expression),
        fieldName: isDateTruncExpression(dimsWithExpressions[dimName].expression)
          ? getDateBucketFromExpression(dimsWithExpressions[dimName].expression)
          : undefined,
        shouldDisplayNull,
      });
    }

    // Handle time-ranged query without an expression
    if (widget.requests[0].timeRange) {
      return generateInnerDefaultFormatter({
        value: datum[dimNameOrAlias],
        type: headCellType,
        fieldName: dimNameOrAlias,
        shouldDisplayNull,
      });
    }

    const isNullOrEmpty = [null, 'null', ''].includes(datum[dimNameOrAlias]);
    if (isNullOrEmpty) {
      return generateInnerDefaultFormatter({
        value: datum[dimNameOrAlias],
        type: getHeadCellType({ entityFieldKey: dimNameOrAlias }),
        shouldDisplayNull,
      });
    }

    return datum[dimNameOrAlias];
  };

  const parsedData: PieWidgetDataEntry[] = useMemo(
    () =>
      widget.type === PieSubType.PieWithNeedle
        ? createPieWithNeedleData(dimNameOrAlias, metric, headCellType)
        : data?.map(datum => ({
            ...datum,
            // @ts-expect-error: Return type of useGetDisplayName callback is possibly null | undefined. this case is unhandled here. should fix this.
            [metricDisplayName]: datum[metric],
            headCellType,
            dim: dimNameOrAlias,
            metric,
            originalValue: dimsWithExpressions[dimName] ? datum[dimName] : datum[dimNameOrAlias],
            [dimNameOrAlias]: getFormattedDimensionValue(datum),
          })),
    [data, widget, getHeadCellType, dimsWithExpressions]
  );

  const defaultPalette = useCustomPalette(data);
  const gaugeCustomPalette = [
    SeverityPrimaryColors[SeverityLabels.Critical],
    SeverityPrimaryColors[SeverityLabels.High],
    SeverityPrimaryColors[SeverityLabels.Medium],
    SeverityPrimaryColors[SeverityLabels.Low],
  ];
  const finalCustomPalette = customPalette || (widget.type === PieSubType.PieWithNeedle ? gaugeCustomPalette : defaultPalette);
  const formattingRuleById = getFormattingRuleById(
    featureFlags[FeatureFlags.FormattingRulesDims]
      ? widget.definition.custom.applyFormattingToAll?.formattingRuleId || widget.definition.custom.fieldStyle?.[metric]?.formattingRuleId
      : widget.definition.custom.fieldStyle?.[metric]?.formattingRuleId,
    formattingRules
  );

  const getFormattedColorCell = (value: Data) => getColorFromFormattingConditions({ value, formattingRule: formattingRuleById });

  const getColorFromSeries = ({
    dimValue,
    metricValue,
    index,
  }: {
    dimValue: string | null | undefined;
    metricValue: number;
    index: number;
  }) =>
    getFormattedColorCell(
      featureFlags[FeatureFlags.FormattingRulesDims]
        ? !!formattingRuleById && isDimensionFormattingRule(formattingRuleById)
          ? dimValue
          : metricValue
        : metricValue
    ) || (dimValue != null && isValidSeverity(dimValue) ? SeverityPrimaryColors[dimValue] : finalCustomPalette[index]);

  const commonProps: AvPieProps = {
    data: parsedData,
    customPalette: finalCustomPalette,
    enableResponsiveLegend,
    severitySetting: widget.type === PieSubType.PieWithNeedle ? getSeverityByScore : undefined,
    legendProps: {
      showValue:
        [PieLegendStyles.Table, PieLegendStyles.Aside].includes(widget.definition.custom.legend.style) &&
        widget.definition.custom.legend.showValues,
      showShare:
        [PieLegendStyles.Table, PieLegendStyles.Aside].includes(widget.definition.custom.legend.style) &&
        widget.definition.custom.legend.showPercentage,
      variant: 'static',
      metricNameFormatter: v => fieldFormatter(v, getHeadCellType({ entityFieldKey: dimNameOrAlias })),
      // maxMetricWidth: labelFormatter === LabelFormatterOption.SOURCE_ICONS_WITH_NAMES ? 200 : 110,
      maxMetricWidth: labelFormatter === LabelFormatterOption.SOURCE_ICONS_WITH_NAMES ? 200 : enableResponsiveLegend ? '100%' : 110,
    } as const,
    ...(widget.type === PieSubType.Donut && { showTotals: !!(widget as DonutTypeWidget).definition.custom.showTotals }),
    responseTotals: responseTotals?.[metric],
    showTable: widget.definition.custom.legend.style === PieLegendStyles.Table,
    showLabels: widget.definition.custom.legend.style === PieLegendStyles.Aside,
    labelFormatter,
    className: 'draggableCancel',
    sx: {
      justifyContent: 'center',
      ...(widget.definition.custom.legend.style !== PieLegendStyles.Basic && { py: 1.5 }),
      ...(widget.type === PieSubType.PieWithNeedle && { pt: 7.5 }),
    },
    onSelect,
    selected: getSelectedPieChartData(selected, parsedData, dimNameOrAlias),
    clickInteractions,
    getFormattedColorCell,
  };

  if (featureFlags[FeatureFlags.NewChartTooltip]) {
    commonProps.tooltipContent = ({ payload, ...props }) => (
      <WidgetTooltipContent
        interactions={clickInteractions}
        widgetData={parsedData}
        payload={payload}
        {...props}
        {...getPieWidgetTooltipContentProps({
          measurements: measurementsObject,
          widget,
          payload,
        })}
      />
    );

    commonProps.rechartsTooltipProps = { offset: 80, allowEscapeViewBox: { x: true, y: false }, useTranslate3d: true };
  }

  const customPieProps = {
    metricKey: dimNameOrAlias,
    valKey: metric,
    valLabel: metricDisplayName?.toString() || '',
    tooltipFields: [metricDisplayName?.toString() || '', 'share'],
    formattingRuleField: formattingRuleById && isDimensionFormattingRule(formattingRuleById) ? dimNameOrAlias : metric,
  };

  const legends = data && (
    <Box
      sx={{
        pb:
          widget.definition.custom.legend.style === PieLegendStyles.Basic &&
          widget.definition.custom.legend.position === PieBasicLegendPosition.Bottom
            ? 2
            : 1,
        pt: '10px',
        cursor: 'default',
      }}
      className="draggableCancel">
      <AvLegend
        series={
          data.map((datum, i) => ({
            ...datum,
            color: getColorFromSeries({ dimValue: datum[dimNameOrAlias], metricValue: datum[metric], index: i }),
            ...(!dimNameOrAlias && metric && { component: <div>{metricDisplayName}</div> }),
            ...(dimNameOrAlias && { component: <div>{datum[dimNameOrAlias] === null ? NO_VALUE : datum[dimNameOrAlias]}</div> }),
          })) as any
        }
        isHorizontal
        nameKey={dimNameOrAlias}
      />
    </Box>
  );

  if (data && useV2 && widget.type !== PieSubType.PieWithNeedle) {
    const customLabelRenderer = getCustomTickFormatter({
      labelFormattingOption: labelFormatter,
      isVertical: false,
      sx: { color: undefined },
      itemMaxWidth: 120,
    });

    return (
      <Box
        sx={{
          ...(widget.definition.custom.legend.style !== PieLegendStyles.Basic && { py: 1.5 }),
          overflow: 'hidden',
          height: '100%',
        }}>
        <PieSeriesAdapter
          data={parsedData}
          widget={widget}
          metric={metric}
          dimNameOrAlias={dimNameOrAlias}
          getColorFromSeries={getColorFromSeries}
          legendProps={commonProps.legendProps}
          clickInteractions={clickInteractions}
          selected={commonProps.selected}
          onSelect={onSelect}
          metricDisplayName={metricDisplayName}
          measurementsObject={measurementsObject}
          customLabelRenderer={customLabelRenderer}
          responseTotals={responseTotals}
        />
      </Box>
    );
  }

  return (
    data && (
      <>
        {widget.definition.custom.legend.style === PieLegendStyles.Basic &&
          widget.definition.custom.legend.position === PieBasicLegendPosition.Top &&
          legends}
        <AvPie {...commonProps} {...customPieProps} type={widget.type} />
        {widget.definition.custom.legend.style === PieLegendStyles.Basic &&
          widget.definition.custom.legend.position === PieBasicLegendPosition.Bottom &&
          legends}
      </>
    )
  );
};

const PieWidget = (props: PieWidgetProps) => {
  const { data: formattingRules = [] } = useFormattingRules();
  const { featureFlags } = useAvContext();
  const measurementsObject = useMetricsContextAsObject();
  const {
    requests: [
      {
        select: { metrics },
        projectionId: { name: projectionIdName },
      },
    ],
  } = props.widget;

  const metric = metrics?.[0]?.alias || metrics[0]?.name;
  const metricDisplayName = useGetDisplayName(projectionIdName)(metric);
  const getHeadCellType = useGetFieldType(projectionIdName);

  return (
    <PieWidgetImpl
      {...props}
      formattingRules={formattingRules}
      measurementsObject={measurementsObject}
      featureFlags={featureFlags}
      metricDisplayName={metricDisplayName}
      getHeadCellType={getHeadCellType}
      useV2={featureFlags[FeatureFlags.PieChartV2]}
    />
  );
};

export default PieWidget;

const getPieWidgetTooltipContentProps = (
  props: Pick<WidgetTooltipContentProps, 'payload'> & { widget: AllTypeWidgets; measurements: Record<string, Measurement> }
) => {
  const { payload, measurements: measurementsObject } = props || {};
  const { payload: cellPayload = {} } = payload?.at(0) || {};
  const { dim, metric, fill, headCellType } = cellPayload;
  const title = dim ? cellPayload[dim] : undefined;
  const value = metric ? cellPayload[metric] : undefined;
  const { description, displayName } = measurementsObject?.[metric] || {};

  const content: WidgetTooltipContentProps['content'] = [{ title: displayName, value, description }];

  return { title: fieldFormatter(title, headCellType), titleDotColor: fill, content };
};
